import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Users, 
  Package, 
  FileText, 
  LogOut, 
  BarChart3, 
  Building2, 
  Ship, 
  Layers,
  TrendingUp,
  AlertCircle,
  DollarSign,
  Activity
} from 'lucide-react';
import { authService } from '../services/authService';
import informeService from '../services/informeService';

function Dashboard() {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadStats();
    
  }, []);

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const loadStats = async () => {
    try {
      setLoading(true);
      const data = await informeService.getDashboardStats();
      setStats(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div className="w-64 bg-gradient-to-b from-slate-900 to-blue-900 text-white">
        <div className="p-6 border-b border-blue-800">
          <div className="flex items-center gap-3">
            <div className="bg-blue-500 p-2 rounded-lg">
              <Ship size={24} />
            </div>
            <div>
              <h1 className="font-bold text-lg">MarineMonitor</h1>
              <p className="text-xs text-blue-200">Multi-Plataforma</p>
            </div>
          </div>
        </div>

        <nav className="p-4 space-y-2">
          <Link
            to="/dashboard"
            className="flex items-center gap-3 px-4 py-3 bg-blue-800 text-white rounded-lg"
          >
            <BarChart3 size={20} />
            <span>Dashboard</span>
          </Link>

          <Link
            to="/equipos"
            className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition"
          >
            <Package size={20} />
            <span>Equipos</span>
          </Link>

          <Link
            to="/tipos-equipo"
            className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition"
          >
            <Layers size={20} />
            <span>Tipos de Equipo</span>
          </Link>

          {user?.rol === 'admin' && (
          <Link
            to="/clientes"
            className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition"
          >
            <Building2 size={20} />
            <span>Clientes</span>
          </Link>
          )}

          {user?.rol === 'admin' && (
            <Link
              to="/users"
              className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition"
            >
              <Users size={20} />
              <span>Usuarios</span>
            </Link>
          )}

          <Link
            to="/solicitudes"
            className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition"
          >
            <FileText size={20} />
            <span>Solicitudes</span>
          </Link>

          <Link
            to="/informes"
            className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition"
          >
            <FileText size={20} />
            <span>Informes</span>
          </Link>
        </nav>

        <div className="absolute bottom-0 w-64 p-4 border-t border-blue-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">{user?.nombre}</p>
              <p className="text-xs text-blue-200">{user?.rol}</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 hover:bg-blue-800 rounded-lg transition"
              title="Cerrar Sesión"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="bg-white shadow-sm p-6">
          <h1 className="text-3xl font-bold text-gray-800">Dashboard</h1>
          <p className="text-gray-600 mt-1">Bienvenido, {user?.nombre}</p>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-xl text-gray-500">Cargando estadísticas...</div>
            </div>
          ) : stats ? (
            <>
              {/* Estadísticas principales */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                {/* Total Equipos */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 font-medium">Total Equipos</p>
                      <p className="text-3xl font-bold text-gray-900 mt-2">{stats.total_equipos}</p>
                    </div>
                    <div className="bg-blue-100 p-3 rounded-lg">
                      <Package className="text-blue-600" size={24} />
                    </div>
                  </div>
                </div>

                {/* Equipos Activos */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 font-medium">Equipos Activos</p>
                      <p className="text-3xl font-bold text-green-600 mt-2">{stats.equipos_activos}</p>
                    </div>
                    <div className="bg-green-100 p-3 rounded-lg">
                      <Activity className="text-green-600" size={24} />
                    </div>
                  </div>
                </div>

                {/* Clientes */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 font-medium">Clientes Activos</p>
                      <p className="text-3xl font-bold text-gray-900 mt-2">{stats.total_clientes}</p>
                    </div>
                    <div className="bg-purple-100 p-3 rounded-lg">
                      <Building2 className="text-purple-600" size={24} />
                    </div>
                  </div>
                </div>

                {/* Facturación Mensual */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 font-medium">Facturación Mensual</p>
                      <p className="text-3xl font-bold text-green-600 mt-2">
                        ${stats.facturacion_mensual_estimada.toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-green-100 p-3 rounded-lg">
                      <DollarSign className="text-green-600" size={24} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Estadísticas secundarias */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                {/* Equipos Inactivos */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm text-gray-600 font-medium">Equipos Inactivos</p>
                    <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-lg font-bold">
                      {stats.equipos_inactivos}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-gray-500 h-2 rounded-full" 
                      style={{ width: `${(stats.equipos_inactivos / stats.total_equipos * 100)}%` }}
                    ></div>
                  </div>
                </div>

                {/* Equipos Suspendidos */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm text-gray-600 font-medium">Equipos Suspendidos</p>
                    <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-lg font-bold">
                      {stats.equipos_suspendidos}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-yellow-500 h-2 rounded-full" 
                      style={{ width: `${(stats.equipos_suspendidos / stats.total_equipos * 100)}%` }}
                    ></div>
                  </div>
                </div>

                {/* Solicitudes Pendientes */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm text-gray-600 font-medium">Solicitudes Pendientes</p>
                    <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-lg font-bold">
                      {stats.solicitudes_pendientes}
                    </span>
                  </div>
                  {stats.solicitudes_pendientes > 0 && (
                    <Link 
                      to="/solicitudes" 
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Ver solicitudes →
                    </Link>
                  )}
                </div>
              </div>

              {/* Actividad reciente */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="text-blue-600" size={20} />
                  <h2 className="text-lg font-bold text-gray-800">Actividad Reciente</h2>
                </div>
                <p className="text-gray-600">
                  {stats.cambios_estado_ultimos_30_dias} cambios de estado en los últimos 30 días
                </p>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;